import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2, ShieldCheck, RefreshCw, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { track } from "@/lib/analytics";

const MAX_AUTO_POLLS = 6; // ~30 detik polling otomatis saat status pending
const POLL_MS = 5000;

// Return target after full-page Snap Redirect (mobile QRIS/GoPay flow).
// Midtrans appends ?order_id&status_code&transaction_status — we NEVER trust
// these for granting access; we confirm server-to-server via /subscription/verify.
// If order_id is missing, the backend falls back to the user's latest order.
export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSubscription } = useApp();
  const orderId = params.get("order_id");
  const [state, setState] = useState("checking");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const polls = useRef(0);
  const tracked = useRef(false);

  const selfUrl = `/payment/return${orderId ? `?order_id=${encodeURIComponent(orderId)}` : ""}`;

  const check = async () => {
    setBusy(true);
    try {
      const res = await api.subscriptionVerify(orderId);
      await refreshSubscription();
      if (res.active) {
        if (!tracked.current) { tracked.current = true; track("premium_unlocked"); }
        setState("active");
      } else if (["pending", "authorize"].includes(res.transactionStatus)) {
        setState("pending");
        if (polls.current < MAX_AUTO_POLLS) {
          polls.current += 1;
          setTimeout(check, POLL_MS);
        }
      } else {
        setState("failed");
      }
    } catch (e) {
      // The return leg can land on a different domain (custom vs platform), where
      // the session cookie doesn't exist — ask the user to log in instead of
      // showing a scary generic error. Their payment is safe server-side.
      if (/authenticated/i.test(e.message || "")) setState("login");
      else setState("error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const content = {
    checking: {
      Icon: Loader2, cls: "text-emerald-500 animate-spin",
      title: "Memverifikasi pembayaran…",
      desc: "Mohon tunggu, kami sedang mengonfirmasi pembayaran Anda ke Midtrans.",
      btn: null,
    },
    active: {
      Icon: CheckCircle2, cls: "text-emerald-500",
      title: "Pembayaran berhasil! 🎉",
      desc: "CeKKeU Premium Anda sudah aktif. Kwitansi dikirim ke email Anda.",
      btn: { label: "Mulai Sekarang", to: "/app/beranda", testid: "return-success-cta" },
    },
    pending: {
      Icon: Clock, cls: "text-amber-500",
      title: "Menunggu konfirmasi pembayaran",
      desc: "Pembayaran Anda sedang diproses. Kami memeriksa otomatis — atau tekan \"Cek Status\" bila Anda sudah membayar (mis. via QRIS di perangkat lain).",
      btn: { label: "Kembali ke Beranda", to: "/app/beranda", testid: "return-pending-cta" },
    },
    failed: {
      Icon: XCircle, cls: "text-rose-500",
      title: "Pembayaran belum berhasil",
      desc: "Pembayaran tidak terkonfirmasi atau dibatalkan. Bila Anda sudah membayar, tekan \"Cek Status\" untuk memeriksa ulang.",
      btn: { label: "Coba Lagi", to: "/paywall", testid: "return-retry-cta" },
    },
    login: {
      Icon: LogIn, cls: "text-emerald-500",
      title: "Masuk untuk melihat status",
      desc: "Pembayaran Anda tetap tercatat aman. Anda kembali lewat alamat yang berbeda, jadi silakan masuk kembali untuk mengonfirmasi status Premium Anda.",
      btn: { label: "Masuk", to: `/login?next=${encodeURIComponent(selfUrl)}`, testid: "return-login-cta" },
    },
    error: {
      Icon: XCircle, cls: "text-rose-500",
      title: "Tidak dapat memverifikasi",
      desc: "Terjadi kendala saat memeriksa pembayaran. Coba lagi sebentar.",
      btn: { label: "Kembali", to: "/paywall", testid: "return-error-cta" },
    },
  }[state];

  const { Icon } = content;
  const showCheckBtn = ["pending", "failed", "error"].includes(state);
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center bg-white px-8 text-center" data-testid="payment-return">
      <ShieldCheck className="mb-2 h-6 w-6 text-emerald-600" />
      <Icon className={`h-14 w-14 ${content.cls}`} />
      <h1 className="mt-5 font-heading text-xl font-extrabold text-zinc-900" data-testid="return-title">{content.title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-zinc-500">{content.desc}</p>
      {showCheckBtn && (
        <button
          data-testid="return-check-status-btn"
          onClick={check}
          disabled={busy}
          className="mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {busy ? "Memeriksa…" : "Cek Status Pembayaran"}
        </button>
      )}
      {content.btn && (
        <button
          data-testid={content.btn.testid}
          onClick={() => navigate(content.btn.to)}
          className={`${showCheckBtn ? "mt-3 border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50" : "mt-8 bg-zinc-900 text-white hover:bg-zinc-800"} h-12 w-full rounded-full text-sm font-semibold`}
        >
          {content.btn.label}
        </button>
      )}
    </div>
  );
}
